import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DateString, isValidDateString } from '../../../common/src/date_string.ts';
import { CustomMusic, Hymn, IntervalType, SongList, SongListEntry, SongListType } from '../../../common/src/model.ts';
import { getSongListStartDate, hasInterval, isBound } from '../../../common/src/model_util.ts';
import { Pages } from '../../../common/src/pages.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { HymnalWithHymns } from '../data/hymnals.ts';
import { useCurrentOrg } from '../data/use_current_org.tsx';
import { useCustomMusic } from '../data/use_custom_music.tsx';
import { useHousehold } from '../data/use_household.tsx';
import { HymnalsContext } from '../data/use_hymnals.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { getHymnFromSongListEntry, getUrlForPage } from '../util/path.ts';
import { headerHeight } from '../util/shared.tsx';
import { SignUpButton } from '../util/sign_up_button.tsx';
import { HymnsList } from '../views/hymns_list.tsx';
import { Header } from './header.tsx';
import {
  DateQueryString,
  IntervalTypeQueryString,
  OrganizationQueryString,
  SongListTypeQueryString,
} from './onboarding/common.tsx';

const eventHymnalSlug = 'EVENT';

// ?organization=229&date=2025-06-20&song-list-type=interval&interval-type=week

export function isEventIsland(page?: Pages, search?: string) {
  if (page !== Pages.EventIsland) {
    return false;
  }
  return getEventIslandParams(search) !== undefined;
}

interface EventIslandParams {
  churchId: number;
  date: DateString;
  songListType: SongListType;
  intervalType?: IntervalType;
}

export function getEventIslandParams(search?: string): EventIslandParams | undefined {
  const urlParams = new URLSearchParams(search);
  const churchId = Number(urlParams.get(OrganizationQueryString));
  const date = urlParams.get(DateQueryString);
  const songListType = urlParams.get(SongListTypeQueryString) as SongListType;
  const intervalType = urlParams.get(IntervalTypeQueryString) as IntervalType | undefined;
  if (
    isNaN(churchId) ||
    !date ||
    !isValidDateString(date) ||
    !Object.values(SongListType).includes(songListType) ||
    (intervalType && !Object.values(IntervalType).includes(intervalType))
  ) {
    return undefined;
  }
  return { churchId, date, songListType, intervalType };
}

export const EventIsland = ({ params }: { params?: EventIslandParams }) => {
  params = params ?? getEventIslandParams(window.location.search);
  const navigate = useNavigate();
  const { currentOrg } = useCurrentOrg();
  const { household } = useHousehold();
  const [weeklySongLists] = useLocalStorage(LocalStorageKey.WeeklySongList);
  const allHymnals = use(HymnalsContext);

  useEffect(() => {
    if (!params) {
      void navigate(getUrlForPage(Pages.Organization));
    }
  }, [navigate, params]);

  // TODO(hewitt): Rename churchId -> orgId for event islands after 7/19 (sdrc event)
  const { churchId, date, songListType, intervalType } = params ?? {};
  const { customMusic, registerAdditionalOrgIds, unregisterAdditionalOrgIds } = useCustomMusic();

  useEffect(() => {
    if (churchId === undefined) {
      return;
    }
    const orgIds = [churchId];
    registerAdditionalOrgIds(orgIds);
    return () => unregisterAdditionalOrgIds(orgIds);
  }, [churchId, registerAdditionalOrgIds, unregisterAdditionalOrgIds]);

  const isMatchingSongList = useCallback(
    (list: SongList) =>
      getSongListStartDate(list) === date &&
      list.type === songListType &&
      (!hasInterval(list) || list.interval.type === intervalType),
    [date, intervalType, songListType],
  );

  const cachedSongList = useMemo(
    () => (currentOrg?.id === churchId ? weeklySongLists.find(isMatchingSongList) : undefined),
    [currentOrg?.id, churchId, isMatchingSongList, weeklySongLists],
  );

  const [songList, setSongList] = useState<SongList | undefined>(() => cachedSongList);
  // const songList = useMemo(() => cachedSongList, [cachedSongList]);

  useEffect(() => {
    if (cachedSongList || !churchId || !date) {
      return;
    }
    void (async () => {
      const serverSongList = await server_api.getSongLists({ churchId, from: date, to: date });
      setSongList(serverSongList.find(isMatchingSongList));
    })();
  }, [cachedSongList, churchId, date, isMatchingSongList]);

  const title = songList?.name ?? 'Event';

  const hymnals: HymnalWithHymns[] = useMemo(
    () => getHymnalsForEvent(songList, allHymnals, customMusic),
    [allHymnals, customMusic, songList],
  );

  const onBack = useCallback(async () => {
    await navigate(getUrlForPage(Pages.Organization));
  }, [navigate]);

  const header = (
    <HeaderWrapper>
      <Header title={title} onBack={household ? onBack : undefined} />
      {!household && (
        <SignUpButtonWrapper>
          <SignUpButton />
        </SignUpButtonWrapper>
      )}
    </HeaderWrapper>
  );

  return <HymnsList customHeader={header} hymnals={allHymnals} weeklySongGroups={hymnals ?? []} title={title} />;
};

const HeaderWrapper = styled.div`
  width: 100%;
`;

const SignUpButtonWrapper = styled.div`
  /* Center vertically */
  top: calc(${headerHeight} / 2);
  transform: translateY(-51%);
  position: absolute;
  right: 1px;
`;

function getHymnalsForEvent(
  songList: SongList | undefined,
  allHymnals: HymnalWithHymns[],
  customMusic: CustomMusic,
): HymnalWithHymns[] {
  if (!songList) {
    return [];
  }

  // first row must be a header if the list is to have headers (unbound song list entry)
  if (songList.songs.length === 0 || isBound(songList.songs[0])) {
    return [
      {
        id: 0,
        name: 'Event',
        hymns: getHymnsFromSongs(songList.songs, allHymnals, customMusic),
        slug: eventHymnalSlug,
        songListType: songList?.type,
        hideTitle: true,
      },
    ];
  }

  // employ unbound song list entries as headers
  const hymnals: HymnalWithHymns[] = [];
  let header = '';
  const songs: SongListEntry[] = [];
  let id = 0;
  for (const [index, song] of songList.songs.entries()) {
    if (isBound(song)) {
      songs.push(song);
    }
    // whenever we encounter a header or get to the end of the list, push another hymnal
    if (!isBound(song) || index === songList.songs.length - 1) {
      if (songs.length > 0) {
        hymnals.push({
          id: id++,
          name: header,
          hymns: getHymnsFromSongs(songs, allHymnals, customMusic),
          slug: eventHymnalSlug,
          songListType: songList?.type,
        });
        songs.length = 0;
      }
      if (!isBound(song)) {
        header = song.text;
      }
    }
  }
  return hymnals;
}

function getHymnsFromSongs(songs: SongListEntry[], hymnals: HymnalWithHymns[], customMusic: CustomMusic): Hymn[] {
  return songs.map((song) => getHymnFromSongListEntry({ song, hymnals, customMusic })).filter((hymn) => hymn) as Hymn[];
}
